import type {VariantType} from '@github-ui/list-view/ListViewVariantContext'
import {CheckCircleFillIcon, NoEntryFillIcon, XCircleFillIcon} from '@primer/octicons-react'
import {Octicon} from '@primer/react/deprecated'
import {Suspense} from 'react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import type {PullRequestReviewDecision} from './__generated__/IssuePullRequestDescription.graphql'
import type {IssueRowSecondaryQuery} from './__generated__/IssueRowSecondaryQuery.graphql'
import type {PullRequestRowSecondary$key} from './__generated__/PullRequestRowSecondary.graphql'
import type {ReviewDecision$key} from './__generated__/ReviewDecision.graphql'
import {IssuesIndexSecondaryGraphqlQuery} from './IssueRow'
import {PullRequestRowSecondaryFragment} from './PullRequestRow'
import styles from './ReviewDecision.module.css'

export const ReviewDecisionFragment = graphql`
  fragment ReviewDecision on PullRequest {
    reviewDecision
  }
`

export function ReviewDecision({
  decision,
  variant = 'default',
}: {
  decision: PullRequestReviewDecision
  variant?: VariantType
}) {
  const reviewDecision = getStyledDecision(decision)
  if (!reviewDecision) return null

  return variant === 'default' ? (
    <div className={styles.reviewDecisionContainer}>
      {' '}
      &middot;
      <span className={styles.reviewDecisionContent}>{reviewDecision}</span>
    </div>
  ) : (
    <div className={styles.reviewDecisionContainer}>
      <span>&middot;</span>
      {reviewDecision}
    </div>
  )
}

function getStyledDecision(decision: string) {
  switch (decision) {
    case 'APPROVED':
      return (
        <>
          <Octicon icon={CheckCircleFillIcon} size={12} className={styles.approvedIcon} />{' '}
          <span className={styles.statusText}>Approved</span>
        </>
      )
    case 'CHANGES_REQUESTED':
      return (
        <>
          <Octicon icon={XCircleFillIcon} size={12} className={styles.changesRequestedIcon} />{' '}
          <span className={styles.statusText}>Changes requested</span>
        </>
      )
    case 'REVIEW_REQUIRED':
      return (
        <>
          <Octicon icon={NoEntryFillIcon} size={12} className={styles.reviewRequiredIcon} />{' '}
          <span className={styles.statusText}>Review required</span>
        </>
      )
    default:
      return null
  }
}

export function LazyReviewDecision({
  id,
  secondaryQueryRef,
  variant,
}: {
  id: string
  secondaryQueryRef?: PreloadedQuery<IssueRowSecondaryQuery> | null
  variant: VariantType
}) {
  if (!secondaryQueryRef) return null

  return (
    <Suspense fallback={null}>
      <LazyReviewDecisionFetched id={id} secondaryQueryRef={secondaryQueryRef} variant={variant} />
    </Suspense>
  )
}

function LazyReviewDecisionFetched({
  id,
  secondaryQueryRef,
  variant,
}: {
  id: string
  secondaryQueryRef: PreloadedQuery<IssueRowSecondaryQuery>
  variant: VariantType
}) {
  const {nodes} = usePreloadedQuery<IssueRowSecondaryQuery>(IssuesIndexSecondaryGraphqlQuery, secondaryQueryRef)
  const prNode = nodes?.find(node => node?.id === id)

  if (!prNode) return null

  return <LazyReviewDecisionInternal secondaryDataKey={prNode} variant={variant} />
}

function LazyReviewDecisionInternal({
  secondaryDataKey,
  variant,
}: {
  secondaryDataKey: PullRequestRowSecondary$key
  variant: VariantType
}) {
  const data = useFragment(PullRequestRowSecondaryFragment, secondaryDataKey)
  const reviewData = useFragment<ReviewDecision$key>(ReviewDecisionFragment, data)
  if (!reviewData?.reviewDecision) return null

  return <ReviewDecision decision={reviewData.reviewDecision} variant={variant} />
}

try{ ReviewDecision.displayName ||= 'ReviewDecision' } catch {}
try{ LazyReviewDecision.displayName ||= 'LazyReviewDecision' } catch {}
try{ LazyReviewDecisionFetched.displayName ||= 'LazyReviewDecisionFetched' } catch {}
try{ LazyReviewDecisionInternal.displayName ||= 'LazyReviewDecisionInternal' } catch {}